import {
  ContainerType,
  FirestoreConstants,
  PathConstants,
  dbPath,
  elementId,
  ElementPath,
  PublicPath,
  PersonalPath,
  GroupPath,
  StanceType,
} from './typings/types';
import { ContainerData } from './dataModel';
import make_uuid from 'react-uuid';
import { Platform } from 'react-native';
import { StackActionType, StackActions } from '@react-navigation/native';
import { getAllPersonalTopics } from './firebaseWrapper/firebaseWrapper';

export function containerTypeToString(
  type: ContainerType,
  localizedStrings: any,
) {
  let result = localizedStrings.none;
  Object.entries(ContainerType).forEach(([key, value]) => {
    if (value === type) {
      result = localizedStrings[key.toLowerCase()];
    }
  });
  return result;
}

const BREADCRUMB_LABEL_MAX_LENGTH = 40;

/**
 * Shortens a piece of text for display as a single breadcrumb item, adding an
 * ellipsis when it was cut off.
 */
export function truncateForBreadcrumb(
  text: string,
  maxLength: number = BREADCRUMB_LABEL_MAX_LENGTH,
): string {
  if (!text) {
    return '';
  }
  const trimmed = text.trim();
  return trimmed.length > maxLength
    ? trimmed.slice(0, maxLength - 1).trimEnd() + '…'
    : trimmed;
}

/**
 * Builds the label shown for one ancestor in a statement's breadcrumb trail.
 * Statements, sources and questions are labeled with their (truncated)
 * content, topics with their name, and anything else (e.g. an argument, which
 * has no text of its own) falls back to its localized type name.
 */
export function getBreadcrumbLabel(
  type: ContainerType,
  data: { content?: string; name?: string } | undefined,
  localizedStrings: any,
  maxLength: number = BREADCRUMB_LABEL_MAX_LENGTH,
): string {
  if (type === ContainerType.CATEGORY && data?.name) {
    return truncateForBreadcrumb(data.name, maxLength);
  }
  if (
    (type === ContainerType.STATEMENT || type === ContainerType.SOURCE || type === ContainerType.QUESTION) &&
    data?.content
  ) {
    return truncateForBreadcrumb(data.content, maxLength);
  }
  return containerTypeToString(type, localizedStrings);
}

export function getLocationFromPath(path: dbPath): PathConstants {
  return path.split('_')[0] as PathConstants;
}

export function getIdFromPath(path: dbPath): elementId {
  return path.split('_').pop() || '';
}

export function getContainerTypeFromId(id: elementId): ContainerType {
  return id.split('-')[0] as ContainerType;
}

export function getContainerTypeFromPath(path: dbPath): ContainerType {
  return getContainerTypeFromId(getIdFromPath(path));
}

export function createPathWithUUID(
  location: PathConstants,
  typeId: ContainerType,
): elementId {
  return location + '_' + typeId + '-' + make_uuid();
}

export function createPathFromId(id: elementId, location: PathConstants): dbPath {
  return location + '_' + id;
}

export function createPersonalPathFromId(id: elementId): dbPath {
  return PathConstants.PERSONAL + '_' + id;
}

export function createPublicPathFromId(id: elementId): elementId {
  return PathConstants.PUBLIC + '_' + id;
}

export function convertToPublicElementInfo(info: ElementPath): ElementPath {
  if (info.id !== FirestoreConstants.ROOT_ID) {
    info.loc = PathConstants.PUBLIC;
    return info;
  } else {
    throw new Error('Cannot convert root element info to public element info');
  }
}

export function convertPathToInfo(path: dbPath, groupId?: string): ElementPath {
  if (groupId) {
    return {
      loc: getLocationFromPath(path),
      id: getIdFromPath(path),
      type: getContainerTypeFromPath(path),
      groupId: groupId,
    } as GroupPath;
  } else {
    return {
      loc: getLocationFromPath(path),
      id: getIdFromPath(path),
      type: getContainerTypeFromPath(path),
    } as PublicPath | PersonalPath | GroupPath;
  }
}

export function convertInfoToPath(info: ElementPath): dbPath {
  if (info.id === FirestoreConstants.ROOT_ID) {
    return FirestoreConstants.ROOT_PATH;
  }
  const typeIncludedInId = info.id[1] === '-';
  if (typeIncludedInId) {
    return info.loc + '_' + info.id;
  } else {
    return info.loc + '_' + info.type + '-' + info.id;
  }
}

export function pathIsPublic(path: dbPath): boolean {
  return path.split('_')[0] === PathConstants.PUBLIC;
}

export function isEditingAllowed(container: ContainerData): boolean {
  return !(
    container.getPath() !== FirestoreConstants.ROOT_PATH &&
    (!container.isOwnedByCurrentUser() || container.isPublic())
  );
}

// Within a group, only the group admin and the statement/argument's own author may add a
// comment to it -- not just any group member. `authorId` is the commented-on container's
// `userId` field, which (see makeContainerInDB) holds either the creating member's memberId or,
// if the admin created it directly, the admin's own uid -- the same "author" concept already
// used to gate editing/deleting a container (see e.g. StatementView's
// `container.getUserId() === memberId` checks). `groupAdminLoggedIn` grants access regardless
// of authorship; otherwise the currently logged in member (`memberId`) must be the author.
export function canAddComment(authorId: string, groupAdminLoggedIn: boolean, memberId?: string): boolean {
  return !!groupAdminLoggedIn || (!!memberId && authorId === memberId);
}

export function applyOnPlatform(web: any, native: any): any {
  if (Platform.OS === 'web') {
    return web;
  } else {
    return native;
  }
}

export function isEmpty(s: string): boolean {
  return s === undefined || s === null || s === '';
}

export function locToUrlString(loc: PathConstants): string {
  switch (loc) {
    case PathConstants.PERSONAL:
      return 'personal';
    case PathConstants.PUBLIC:
      return 'public';
    case PathConstants.GROUP:
      return 'group';
    default:
      throw new Error('Invalid location');
  }
}

export function typeToUrlString(type: ContainerType): string {
  switch (type) {
    case ContainerType.NONE:
      return 'none';
    case ContainerType.STATEMENT:
      return 'statement';
    case ContainerType.SOURCE:
      return 'source';
    case ContainerType.ARGUMENT:
      return 'argument';
    case ContainerType.CATEGORY:
      return 'topic';
    case ContainerType.QUESTION:
      return 'question';
    default:
      throw new Error('Invalid type');
  }
}

export function urlStringToLoc(urlString: string): PathConstants {
  switch (urlString) {
    case 'personal':
      return PathConstants.PERSONAL;
    case 'public':
      return PathConstants.PUBLIC;
    case 'group':
      return PathConstants.GROUP;
    case 'groups':
      return PathConstants.GROUP;
    default:
      if (Object.values(PathConstants).includes(urlString as PathConstants)) {
        return urlString as PathConstants;
      } else {
        throw new Error('Invalid location');
      }
  }
}

export function urlStringToType(urlString: string): ContainerType {
  switch (urlString) {
    case 'none':
      return ContainerType.NONE;
    case 'statement':
      return ContainerType.STATEMENT;
    case 'source':
      return ContainerType.SOURCE;
    case 'argument':
      return ContainerType.ARGUMENT;
    case 'topic':
      return ContainerType.CATEGORY;
    case 'group':
      return ContainerType.CATEGORY;
    case 'question':
      return ContainerType.QUESTION;
    default:
      if (Object.values(ContainerType).includes(urlString as ContainerType)) {
        return urlString as ContainerType;
      } else {
        throw new Error('Invalid type');
      }
  }
}

export function convertInfoToRouteParams(info: ElementPath, memberId?: string): any {
  if ('groupId' in info) {
    if (memberId) {
      return {
        loc: locToUrlString(info.loc),
        id: info.id,
        type: typeToUrlString(info.type),
        groupId: info.groupId,
        member: memberId
      };
    } else {
      return {
        loc: locToUrlString(info.loc),
        id: info.id,
        type: typeToUrlString(info.type),
        groupId: info.groupId
      };
    }
  } else {
    return {
      loc: locToUrlString(info.loc),
      id: info.id,
      type: typeToUrlString(info.type),
    };
  }
}

export function convertRouteParamsToInfo(params: any, location?: string, id?: string, type?: string, groupId?: string): ElementPath {
  if (params && 'groupId' in params) {
    return {
      loc: urlStringToLoc(params.loc),
      id: params.id,
      type: urlStringToType(params.type),
      groupId: params.groupId
    } as ElementPath;
  } else if (params) {
    return {
      loc: urlStringToLoc(params.loc),
      id: params.id,
      type: urlStringToType(params.type),
    } as ElementPath;
  } else if (location && id && type && groupId) {
    return {
      loc: urlStringToLoc(location),
      id: id,
      type: urlStringToType(type),
      groupId: groupId
    } as ElementPath;
  } else if (location && id && type) {
    return {
      loc: urlStringToLoc(location),
      id: id,
      type: urlStringToType(type),
    } as ElementPath
  } else {
    throw new Error('Invalid route parameters');
  }
}

export function createStackActionFromInfo(info: ElementPath): StackActionType {
  let screen = '';
  switch (info.loc) {
    case PathConstants.PERSONAL:
      screen = 'PersonalStatementView';
      break;
    case PathConstants.PUBLIC:
      screen = 'PublicStatementView';
      break;
    case PathConstants.GROUP:
      screen = 'GroupStatementView';
      break;
    default:
      throw new Error('Invalid location');
  }
  return StackActions.push(screen, info);
}

export function selectScreenFromInfo(info: ElementPath): string {
  switch (info.loc) {
    case PathConstants.PERSONAL:
      return 'PersonalStatementView';
    case PathConstants.PUBLIC:
      if (info.type === ContainerType.QUESTION) {
        return 'question'
      } else {
        return 'PublicStatementView';
      }
    case PathConstants.GROUP:
      if (info.type === ContainerType.ARGUMENT) {
        return 'argument';
      } else if (info.type === ContainerType.QUESTION) {
        return 'question';
      } else {
        return 'GroupStatementView';
      }
    default:
      throw new Error('Invalid location');
  }
}

export function containerToLink(c: ContainerData): string {
  return `https://app.lucid-mind.eu/${locToUrlString(c.getLocation())}/${typeToUrlString(c.getType())}/${c.getId()}`;
}

export async function getTopics(topicNames: string[]): Promise<Object[]> {
  const topics = [];
  const existingTopics = await getAllPersonalTopics();
  for (const name of topicNames) {
    if (name === '') {
      throw new Error('Topic name cannot be empty');
    }
    const matchingExistingTopics = existingTopics.filter(existingTopic => (existingTopic as { id: string, name: string }).name === name);
    if (matchingExistingTopics.length > 0) {
      topics.push(matchingExistingTopics[0]);
    }
  }
  return topics
}

/**
 * For a gradation Question (e.g. "Is climate change man-made?" -> "Yes 100%" / "Yes 60%" /
 * "No only 10%"), the answers are just degrees of the *same* underlying claim, each tagged
 * SUPPORT/OPPOSE relative to the Question (chosen via the create dialog's stance toggle). An
 * argument attached to one of those answers is itself tagged SUPPORT/OPPOSE relative to that
 * answer. Combining the two gives the argument's effective position on the underlying claim
 * (its "conclusion"): supporting a supportive answer, or opposing an opposing one, both argue
 * the same side; the other two combinations argue the other side. Arguments that land on the
 * same side share the same conclusion; arguments on opposite sides have opposing conclusions.
 *
 * Purely structural (no text/semantic matching) -- it only combines StanceType values that
 * already exist on the Question's and its answers' childData. Falls back to NEUTRAL when either
 * stance is unknown (e.g. legacy data), which callers should treat as "unmatched".
 *
 * @param answerStance The answer's own stance relative to the Question.
 * @param argumentStanceRelativeToAnswer The argument's stance relative to that answer.
 */
export function computeGradationArgumentConclusionStance(
  answerStance: StanceType | undefined,
  argumentStanceRelativeToAnswer: StanceType | undefined,
): StanceType {
  if (answerStance === undefined || argumentStanceRelativeToAnswer === undefined) {
    return StanceType.NEUTRAL;
  }
  return answerStance === argumentStanceRelativeToAnswer
    ? StanceType.SUPPORT
    : StanceType.OPPOSE;
}

/**
 * Batch form of {@link computeGradationArgumentConclusionStance}: given, for every argument
 * attached to some answer within a gradation Question's answer set, which answer it's attached
 * to, plus lookups for each answer's stance (relative to the Question) and each argument's own
 * stance (relative to its answer), returns each argument's effective conclusion stance keyed by
 * its path. This is the "detect/match arguments with the same or opposing conclusion" step --
 * grouping/rendering is left to the caller.
 */
export function computeGradationArgumentConclusionStances(
  argumentToAnswerPaths: { argumentPath: dbPath; answerPath: dbPath }[],
  answerStanceByPath: { [answerPath: dbPath]: StanceType },
  argumentStanceRelativeToAnswerByPath: { [argumentPath: dbPath]: StanceType },
): { [argumentPath: dbPath]: StanceType } {
  const result: { [argumentPath: dbPath]: StanceType } = {};
  argumentToAnswerPaths.forEach(({ argumentPath, answerPath }) => {
    result[argumentPath] = computeGradationArgumentConclusionStance(
      answerStanceByPath[answerPath],
      argumentStanceRelativeToAnswerByPath[argumentPath],
    );
  });
  return result;
}

export function getLocation(navigation: any): PathConstants {
  const locations = {
    "FeedList": PathConstants.PUBLIC,
    "groups": PathConstants.GROUP,
    "PersonalList": PathConstants.PERSONAL
  }
  const routes: Array<any> = navigation?.getState()?.routes;
  const routeEntryPoint = routes[0];
  const location = locations[routeEntryPoint?.name];
  return location as PathConstants;
}

// Called with a premise/condition TextArea's latest value on every keystroke. When the
// keystroke that produced it was Enter (the value now ends with a newline), the newline
// is stripped back off and, if there is non-blank content to keep, `addUnit` signals the
// caller to start a new premise -- the same outliner-style behavior as pressing the
// existing "add condition" button, but triggered from the keyboard.
export function handleConditionContentChange(value: string): { content: string; addUnit: boolean } {
  if (!value.endsWith('\n')) {
    return { content: value, addUnit: false };
  }
  const content = value.slice(0, -1);
  return { content, addUnit: content.trim().length > 0 };
}